import { isEnabled } from 'Config';
import {
  LEVER_TYPE_BOOLEAN,
  LEVER_TYPE_NUMBER,
  LEVER_TYPE_STRING,
} from 'Lib/ConfigService/constants';
import type { LeverConfigType, LeverConfigValue } from 'Lib/ConfigService/types';

import { MARKETING_EMAIL_CONSENT_TYPE_OPT_IN } from 'Store/email/constants';
import { AUTH_OPTIONS_VARIANT_GOOGLE_FB_SMS } from 'Store/mfa/constants';

import { INDICATOR_RED_DOT } from 'Components/MessageListItem/constants';

/**
 * Lever definitions
 * =================
 *
 * In the future, these will be codegen-ed from the levers stored in the backened.
 *
 * Enumerating the levers in this way makes it easier to automatically register levers and their
 * fallback values and avoid referencing invalid levers in component code.
 */

export const LEVER_ACCOUNT_DELETION_FULL_PAGE = 'identity.account_deletion_full_page';
export const LEVER_AGE_GATE_JUMIO_APPEAL_ENABLED = 'onboarding.age_gate_jumio_appeal_enabled';
export const LEVER_AGE_VERIFICATION_POST_ONBOARDING_ENABLED =
  'age_verification.post_onboarding_enabled';
export const LEVER_ALC_DISCOUNT_OFFER_PAYWALL_ENABLED =
  'paywall.alc_discount_offer_paywall_enabled';
export const LEVER_ALC_DISCOUNT_OFFER_PAYWALL_COOL_DOWN_PERIOD =
  'paywall.alc_discount_offer_paywall_cool_down_period_in_seconds';
export const LEVER_ALC_DISCOUNT_OFFER_PAYWALL_RATE_LIMIT_VALUE =
  'paywall.alc_discount_offer_paywall_rate_limit_value';
export const LEVER_APPEALS_CENTER_ENABLED = 'trust.appeal_center_access_control';
export const LEVER_AUTH_OPTIONS = 'identity.auth_options';
export const LEVER_BACKEND_DRIVEN_WEB_CAPTCHA_ENABLED =
  'identity.backend_driven_web_captcha_enabled';
export const LEVER_BOOST_WALLET_ENABLED = 'boost.wallet_enabled';
export const LEVER_BOOST_SUBSCRIPTION_PAYWALL_ENABLED = 'paywall.boost_subscription_enabled';
export const LEVER_BOUNCER_BYPASS_ENABLED = 'bouncer.bouncer_bypass_enabled';
export const LEVER_BOUNCER_BYPASS_MODAL_VARIANT = 'bouncer.bouncer_bypass_modal_variant';
export const LEVER_BOUNCER_TO_GOLD = 'bouncer.bounce_to_gold';
export const LEVER_BUGSNAG_IS_ENABLED = 'bugsnag.is_enabled_web';
export const LEVER_BUGSNAG_PERFORMANCE_IS_ENABLED = 'bugsnag.is_performance_enabled_web';
export const LEVER_BUGSNAG_SCOPE_FILTERS = 'bugsnag.scope_filters';
export const LEVER_BUMPER_STICKER_ENABLED = 'profile.bumper_sticker_enabled';
export const LEVER_CANCEL_SAVE_OFFER_ENABLED = 'purchase.cancel_save_offer_enabled_web';
export const LEVER_CONNECTED_ACCOUNTS_ENABLED = 'identity.connected_accounts_enabled';
export const LEVER_CONTEXTUAL_SWIPE_NOTE_RECEIVE_ENABLED = 'contextual_swipe_note_receive.enabled';
export const LEVER_CC_DISCOUNT = 'purchase.cc_discount';
export const LEVER_CHAT_SHIELD_FAB_MESSAGE_CONTROLS_ENABLED =
  'safety.chat_shield_fab_message_controls_enabled';
export const LEVER_COLLECTIBLES_VIEW_ENABLED = 'collectibles.collectibles_view_enabled';
export const LEVER_DSA_CUSTOM_BAN_SCREEN = 'trust.dsa_custom_ban_screen';
export const LEVER_IDV_DYNAMIC_CONSENTS_ENABLED = 'trust.idv_dynamic_consent_enabled';
export const LEVER_SV_DYNAMIC_CONSENTS_ENABLED = 'trust.sv_dynamic_consent_enabled';
export const LEVER_EUREKA_AV_ENABLED = 'age_verification.eureka_age_verification_enabled';
export const LEVER_EVENTS_SDK_ERROR_LOG_SAMPLING_RATE = 'events.sdk_error_log_sampling_rate';
export const LEVER_EVENTS_SDK_IS_ENABLED = 'events.sdk_is_enabled_web';
export const LEVER_EVENTS_SDK_PAYWALL_HUBBLE_IS_ENABLED =
  'events.sdk_paywall_hubble_is_enabled_web';
export const LEVER_EVENTS_SDK_RECS_HUBBLE_IS_ENABLED = 'events.sdk_recs_hubble_is_enabled_web';
export const LEVER_EXPLORE_IS_AVAILABLE = 'explore.is_available';
export const LEVER_FRIENDS_OF_FRIENDS_VARIANT = 'identity.friends_of_friends';
export const LEVER_GLOBAL_PASSPORT_ENABLED = 'passport.global_enabled';
export const LEVER_GLOBAL_PASSPORT_MODAL_ENABLED = 'passport.global_modal_enabled';
export const LEVER_GLOBAL_PASSPORT_MODAL_IMAGE = 'passport.global_modal_image';
export const LEVER_GOLD_NEW_LIKES_INSENDIO_MODAL_ENABLED = 'gold.new_likes_insendio_modal_enabled';
export const LEVER_GOLD_NEW_LIKES_MODAL_FREQUENCY = 'gold.new_likes_modals_frequency';
export const LEVER_INCOGNITO_SETTINGS_VARIANT = 'incognito.settings_variant_web';
export const LEVER_GOLD_WEEKLY_INTRO_OFFER_ENABLED = 'gold.weekly_intro_offer_enabled';
export const LEVER_GOLD_SUBSCRIPTION_DISCOUNT_ENABLED = 'gold.subscription_discount_enabled';
export const LEVER_HIDE_WEB_PROFILE_APP_DOWNLOAD = 'web_profile.hide_app_download_links';
export const LEVER_LIKES_YOU_FILTERING_ENABLED = 'likes_you.filtering_enabled';
export const LEVER_LIKES_YOU_FILTERING_MIN_LIKES_COUNT =
  'likes_you.min_likes_count_to_enable_filtering';
export const LEVER_LIKES_QUICK_FILTER_PASSIONS = 'likes_you.quick_filter_passions';
export const LEVER_LINE_CHANNEL_ID = 'identity.line_channel_id';
export const LEVER_MEDIA_SHORT_VIDEO_VIEWING_ENABLED = 'media.short_video_viewing_enabled';
export const LEVER_ONBOARDING_ALL_IN_MULTI_SELECT_ENABLED = 'onboarding.all_in_multi_select';
export const LEVER_ONBOARDING_BIOMETRICS_LIVENESS_ENABLED =
  'onboarding.biometrics_liveness_enabled';
export const LEVER_ONBOARDING_RELATIONSHIP_INTENT_ENABLED =
  'onboarding.relationship_intent_enabled';
export const LEVER_ONBOARDING_DISMISS_ENABLED = 'onboarding.is_onboarding_dismiss_enabled';
export const LEVER_ONBOARDING_COPY_UPDATES = 'onboarding.is_onboarding_copy_updates_enabled';
export const LEVER_ONBOARDING_SHOW_ME_IN_SEARCHES_FOR_ENABLED =
  'onboarding.show_me_in_searches_for_enabled';
export const LEVER_PAYWALL_LEGAL_AGREEMENT_ENABLED = 'paywall.show_terms_with_refund';
export const LEVER_PLATINUM_BOUNCER_CTA_ENABLED = 'platinum.bouncer_paywall_cta_enabled';
export const LEVER_PLATINUM_GOLD_BOUNCER_CTA_ENABLED = 'platinum.gold_bouncer_paywall_cta_enabled';
export const LEVER_PLATINUM_MY_LIKES_ENABLED = 'platinum.my_likes_enabled';
export const LEVER_PLATINUM_PRIORITY_LIKES_BANNER_ENABLED = 'platinum.match_list_upsell_enabled';
export const LEVER_POST_MATCH_UNREAD_MESSAGE_TREATMENT = 'postmatch.unread_message_treatment';
export const LEVER_PREPURCHASE_LEGAL_AGREEMENT_ENABLED =
  'purchases.require_prepurchase_legal_agreement';
export const LEVER_PRIMETIME_BOOST_ENABLED = 'boost.primetime_enabled';
export const LEVER_PRIMETIME_BOOST_COOLDOWN_INTERVAL = 'boost.primetime_upsell_cooldown_interval';
export const LEVER_PRIORITY_LIKES_INTERVAL = 'platinum.priority_likes_modal_time_interval';
export const LEVER_PRIORITY_LIKES_SWIPE_THRESHOLD = 'platinum.priority_likes_swipe_right_threshold';
export const LEVER_PREFERENCES_IS_ENABLED = 'preferences.is_enabled';
export const LEVER_PROFILE_ALL_IN_MULTI_SELECT = 'profile.all_in_multi_select';
export const LEVER_PROFILE_CUSTOM_GENDER_ENABLED = 'profile.custom_gender_available';
export const LEVER_PROFILE_ELEMENTS_RELATIONSHIP_INTENT_ENABLED =
  'profile_elements.relationship_intent_enabled';
export const LEVER_PROFILE_MFP_ENABLED = 'profile.mfp_enabled';
export const LEVER_PROFILE_MFP_BOUNCER_ENABLED = 'profile.mfp_bouncer_enabled';
export const LEVER_PROFILE_MINIMUM_2_PICTURES = 'profile.minimum_2_pictures';
export const LEVER_PROFILE_RELATIONSHIP_INTENT_PROFILE_DETAIL_ENABLED =
  'profile.relationship_intent_profile_detail';
export const LEVER_PROFILE_SHOW_ME_IN_SEARCHES_FOR_ENABLED =
  'profile.show_me_in_searches_for_enabled';
export const LEVER_PROFILE_SPARKS_MILESTONE_1_ENABLED = 'profile.sparks_milestone_1_enabled';
export const LEVER_PROFILE_ALL_IN_STICKY_EXPERIMENT = 'profile.all_in_sticky_experiment';
export const LEVER_PURCHASE_SHOW_PAYMENT_OPTIONS = 'purchase.show_payment_options';
export const LEVER_PURCHASE_SHOW_CHECKOUT_PAYMENT_OPTIONS =
  'purchase.show_checkout_payment_options';
export const LEVER_PURCHASE_ENABLE_NEW_PAYMENT_ENDPOINTS = 'purchase.enable_new_payment_endpoints';
export const LEVER_PURCHASE_PAYWALL_SHOW_FULL_PRICE = 'purchases.show_full_price_web';
export const LEVER_MARKETING_EMAIL_CONSENT_TYPE =
  'regulatory_affairs.pre_auth_marketing_email_consent_type';
export const LEVER_MATCH_EXPIRATION_BANNER_ENABLED = 'postmatch.match_expiration_banner_enabled';
export const LEVER_MATCH_EXPIRATION_BLUR_EXPIRED_MATCH_MESSAGES =
  'postmatch.blur_expired_match_messages';
export const LEVER_MATCH_EXPIRATION_ENABLED = 'postmatch.match_expiration_enabled';
export const LEVER_MATCH_EXPIRATION_EXTEND_ENABLED = 'postmatch.match_expiration_extend_enabled';
export const LEVER_MATCH_EXPIRATION_URGENCY_PERIOD_SECONDS =
  'postmatch.match_expiration_urgency_period_seconds';
export const LEVER_MESSAGE_PROCESSING_CONSENT_MODAL =
  'chat.message_processing_consent_modal_enabled';
export const LEVER_MESSAGE_SUGGESTIONS_TEST_VARIANT = 'chat.tinder_suggestions_test_variant';
export const LEVER_MESSAGE_SUGGESTIONS_EMPTY_CHAT_ENABLED =
  'chat.empty_chat_suggestions_box_enabled';
export const LEVER_MESSAGE_SUGGESTIONS_INLINE_ENABLED = 'chat.inline_suggestions_box_enabled';
export const LEVER_MLC_ONBOARDING_DYNAMIC_CONSENT_ENABLED = 'trust.mlc_n_dynamic_consent_enabled';
export const LEVER_MLC_POST_REQ_BIOMETRICS_NEEDED_DYNAMIC_CONSENT_ENABLED =
  'trust.mlc_eu_two_dynamic_consent_enabled';
export const LEVER_MLC_POST_REQ_DYNAMIC_CONSENT_ENABLED =
  'trust.mlc_vu_two_dynamic_consent_enabled';
export const LEVER_REPLY_REMINDER_ALLOW_EXPIRED_MATCHES = 'reply_reminder.allow_expired_matches';
export const LEVER_REPLY_REMINDER_SWIPE_COUNT = 'reply_reminder.swipe_count';
export const LEVER_REPORT_SOMEONE_VERSION = 'reporting.report_someone_version';
export const LEVER_SAFETY_AND_REPORTING_LINK_ENABLED = 'trust.safety_reporting_contact_link';
export const LEVER_SAFETY_CENTER_ENABLED = 'safety.chat_reporting_menu_options';
export const LEVER_SELFIE_DELETE_PROMPT_ENABLED = 'trust.selfie_delete_prompt_enabled';
export const LEVER_SELFIE_VERIFICATION_ENABLED = 'trust.selfie_verification_enabled';
export const LEVER_SELECT_ONBOARDING_ACCESS_RESTRICTED = 'select_onboarding.access_restricted';
export const LEVER_SELFIE_VERIFICATION_BIOMETRIC_CONSENT_SCREEN_VERSION =
  'trust.selfie_v2_biometric_consent_screen_version';
export const LEVER_ID_VERIFICATION_VARIANT = 'trust.idv_variant';
export const LEVER_SHIELD_FAB_GARBO_ENABLED = 'safety.safety_center_garbo_enabled';
export const LEVER_SHIELD_FAB_REPORT_USER_ENABLED = 'safety.chat_shield_fab_reporting_enabled';
export const LEVER_SHOW_WEEKLY_SAVINGS_ENABLED = 'pricing.show_weekly_savings';
export const LEVER_SMS_AUTH_LOGIN_SUPPRESSED = 'identity.sms_auth_login_suppressed';
export const LEVER_SPARKS_WEB = 'sparks.web';
export const LEVER_SUB_DISCOUNT_PERSONALIZED_OFFER_ENABLED =
  'subscription_discount.personalized_offer_enabled';
export const LEVER_THEME_OVERRIDE = 'obsidian.theme_override';
export const LEVER_USER_BLOCKING_ENABLED = 'trust.user_blocking_enabled';
export const LEVER_AUTH_GUEST_ARKOSE_ENABLED = 'identity.auth_guest_arkose_enabled';
export const LEVER_INSTAGRAM_IS_ENABLED = 'profile.instagram_enabled';
export const LEVER_SPOTIFY_IS_ENABLED = 'spotify.is_enabled';
export const LEVER_SUPERLIKE_ATTACH_MESSAGE_RATE_LIMIT = 'superlike.attach_message_rate_limit';
export const LEVER_SUPERLIKE_ATTACH_MESSAGE_TARGETING = 'superlike.attach_message_targeting';
export const LEVER_SUPERLIKE_ATTACH_MESSAGE_TIME_LIMIT = 'superlike.attach_message_time_limit_sec';
export const LEVER_SUPERLIKE_UPSELL_HIGH_RSRR_ENABLED = 'superlike.upsell_high_rsrr_enabled';
export const LEVER_SUPERLIKE_UPSELL_HIGH_RSRR_RATE_LIMIT = 'superlike.upsell_rate_limit_unit';
export const LEVER_FIRST_IMPRESSIONS_ENABLED = 'first_impression.enabled';
export const LEVER_TOP_NAV_V2_DYNAMIC_ORDER = 'session.top_nav_v2_dynamic_order';
export const LEVER_WEB_PAYMENT_ENABLED = 'platform.web_payment_enabled';
export const LEVER_WEB_DISCOUNT_BANNER_ENABLED = 'discount.banner_enabled';
export const LEVER_WEB_DISCOUNT_LOGIN_BANNER_ENABLED = 'discount.login_banner_enabled';
export const LEVER_WEB_DISCOUNT_PILL_ENABLED = 'discount.checkout_pill_enabled';
export const LEVER_WEEKLY_GOLD_TO_PLATINUM_OFFER_ENABLED =
  'platinum.weekly_gold_to_plat_offer_enabled';
export const LEVER_MATCHMAKER_SUPERLIKES_OFFER = 'matchmaker.superlikes_offer';

const defaultLineClientId =
  __TEST__ || __DEVELOPMENT__ || __PRODTEST__ ? '1657048357' : '1657787365';

export const REGISTERED_LEVER_VALUES = {
  [LEVER_ACCOUNT_DELETION_FULL_PAGE]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_AGE_GATE_JUMIO_APPEAL_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_AGE_VERIFICATION_POST_ONBOARDING_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_ALC_DISCOUNT_OFFER_PAYWALL_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_ALC_DISCOUNT_OFFER_PAYWALL_COOL_DOWN_PERIOD]: [129600, LEVER_TYPE_NUMBER],
  [LEVER_ALC_DISCOUNT_OFFER_PAYWALL_RATE_LIMIT_VALUE]: [1, LEVER_TYPE_NUMBER],
  [LEVER_APPEALS_CENTER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_AUTH_GUEST_ARKOSE_ENABLED]: [isEnabled('arkoseGuestAuth'), LEVER_TYPE_BOOLEAN],
  [LEVER_AUTH_OPTIONS]: [AUTH_OPTIONS_VARIANT_GOOGLE_FB_SMS, LEVER_TYPE_NUMBER],
  [LEVER_BACKEND_DRIVEN_WEB_CAPTCHA_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_BOOST_WALLET_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_BOOST_SUBSCRIPTION_PAYWALL_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_BOUNCER_BYPASS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_BOUNCER_BYPASS_MODAL_VARIANT]: [0, LEVER_TYPE_NUMBER],
  [LEVER_BOUNCER_TO_GOLD]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_BUGSNAG_IS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_BUGSNAG_PERFORMANCE_IS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_BUGSNAG_SCOPE_FILTERS]: ['{"default": 0.01}', LEVER_TYPE_STRING],
  [LEVER_BUMPER_STICKER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_CANCEL_SAVE_OFFER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_CC_DISCOUNT]: [10, LEVER_TYPE_NUMBER],
  [LEVER_CHAT_SHIELD_FAB_MESSAGE_CONTROLS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_COLLECTIBLES_VIEW_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_DSA_CUSTOM_BAN_SCREEN]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_IDV_DYNAMIC_CONSENTS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SV_DYNAMIC_CONSENTS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_CONNECTED_ACCOUNTS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_CONTEXTUAL_SWIPE_NOTE_RECEIVE_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_EUREKA_AV_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_EVENTS_SDK_ERROR_LOG_SAMPLING_RATE]: [0, LEVER_TYPE_NUMBER],
  [LEVER_EVENTS_SDK_IS_ENABLED]: [true, LEVER_TYPE_BOOLEAN],
  [LEVER_EVENTS_SDK_PAYWALL_HUBBLE_IS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_EVENTS_SDK_RECS_HUBBLE_IS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_EXPLORE_IS_AVAILABLE]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_FRIENDS_OF_FRIENDS_VARIANT]: [0, LEVER_TYPE_NUMBER],
  [LEVER_GLOBAL_PASSPORT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_GLOBAL_PASSPORT_MODAL_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_GLOBAL_PASSPORT_MODAL_IMAGE]: ['', LEVER_TYPE_STRING],
  [LEVER_GOLD_NEW_LIKES_MODAL_FREQUENCY]: [0, LEVER_TYPE_NUMBER],
  [LEVER_GOLD_NEW_LIKES_INSENDIO_MODAL_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_GOLD_WEEKLY_INTRO_OFFER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_GOLD_SUBSCRIPTION_DISCOUNT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_HIDE_WEB_PROFILE_APP_DOWNLOAD]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_INCOGNITO_SETTINGS_VARIANT]: [0, LEVER_TYPE_NUMBER],
  [LEVER_LIKES_YOU_FILTERING_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_LIKES_YOU_FILTERING_MIN_LIKES_COUNT]: [5, LEVER_TYPE_NUMBER],
  [LEVER_LIKES_QUICK_FILTER_PASSIONS]: ['it_54,it_7,it_9', LEVER_TYPE_STRING],
  [LEVER_LINE_CHANNEL_ID]: [defaultLineClientId, LEVER_TYPE_STRING],
  [LEVER_MARKETING_EMAIL_CONSENT_TYPE]: [MARKETING_EMAIL_CONSENT_TYPE_OPT_IN, LEVER_TYPE_STRING],
  [LEVER_MATCH_EXPIRATION_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MATCH_EXPIRATION_BANNER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MATCH_EXPIRATION_BLUR_EXPIRED_MATCH_MESSAGES]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MATCH_EXPIRATION_EXTEND_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MATCH_EXPIRATION_URGENCY_PERIOD_SECONDS]: [86400, LEVER_TYPE_NUMBER],
  [LEVER_MESSAGE_PROCESSING_CONSENT_MODAL]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MESSAGE_SUGGESTIONS_TEST_VARIANT]: ['', LEVER_TYPE_STRING],
  [LEVER_MESSAGE_SUGGESTIONS_EMPTY_CHAT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MESSAGE_SUGGESTIONS_INLINE_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MLC_ONBOARDING_DYNAMIC_CONSENT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MLC_POST_REQ_BIOMETRICS_NEEDED_DYNAMIC_CONSENT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MLC_POST_REQ_DYNAMIC_CONSENT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_REPLY_REMINDER_ALLOW_EXPIRED_MATCHES]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_REPLY_REMINDER_SWIPE_COUNT]: [-1, LEVER_TYPE_NUMBER], // default: -1 (reply reminder disabled)
  [LEVER_REPORT_SOMEONE_VERSION]: [1, LEVER_TYPE_NUMBER],
  [LEVER_MEDIA_SHORT_VIDEO_VIEWING_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PROFILE_MINIMUM_2_PICTURES]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_ONBOARDING_ALL_IN_MULTI_SELECT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_ONBOARDING_BIOMETRICS_LIVENESS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_ONBOARDING_RELATIONSHIP_INTENT_ENABLED]: [
    isEnabled('relationshipIntent'),
    LEVER_TYPE_BOOLEAN,
  ],
  [LEVER_ONBOARDING_DISMISS_ENABLED]: [true, LEVER_TYPE_BOOLEAN],
  [LEVER_ONBOARDING_COPY_UPDATES]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_ONBOARDING_SHOW_ME_IN_SEARCHES_FOR_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_USER_BLOCKING_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PAYWALL_LEGAL_AGREEMENT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PLATINUM_BOUNCER_CTA_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PLATINUM_GOLD_BOUNCER_CTA_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PLATINUM_MY_LIKES_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PLATINUM_PRIORITY_LIKES_BANNER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PREFERENCES_IS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PREPURCHASE_LEGAL_AGREEMENT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PRIMETIME_BOOST_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PRIMETIME_BOOST_COOLDOWN_INTERVAL]: [0, LEVER_TYPE_NUMBER],
  [LEVER_PRIORITY_LIKES_INTERVAL]: [28800, LEVER_TYPE_NUMBER],
  [LEVER_PRIORITY_LIKES_SWIPE_THRESHOLD]: [10, LEVER_TYPE_NUMBER],
  [LEVER_PROFILE_ALL_IN_MULTI_SELECT]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PROFILE_CUSTOM_GENDER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PROFILE_ELEMENTS_RELATIONSHIP_INTENT_ENABLED]: [
    isEnabled('relationshipIntent'),
    LEVER_TYPE_BOOLEAN,
  ],
  [LEVER_PROFILE_RELATIONSHIP_INTENT_PROFILE_DETAIL_ENABLED]: [
    isEnabled('relationshipIntent'),
    LEVER_TYPE_BOOLEAN,
  ],
  [LEVER_PROFILE_SHOW_ME_IN_SEARCHES_FOR_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PROFILE_ALL_IN_STICKY_EXPERIMENT]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PROFILE_MFP_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PROFILE_MFP_BOUNCER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PROFILE_SPARKS_MILESTONE_1_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PURCHASE_SHOW_PAYMENT_OPTIONS]: [false, LEVER_TYPE_BOOLEAN],
  // controls unread message treatment: 0 = red dot (default), 1 = bold text
  [LEVER_POST_MATCH_UNREAD_MESSAGE_TREATMENT]: [INDICATOR_RED_DOT, LEVER_TYPE_NUMBER],
  [LEVER_PURCHASE_SHOW_CHECKOUT_PAYMENT_OPTIONS]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PURCHASE_ENABLE_NEW_PAYMENT_ENDPOINTS]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_PURCHASE_PAYWALL_SHOW_FULL_PRICE]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SAFETY_AND_REPORTING_LINK_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SAFETY_CENTER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SELECT_ONBOARDING_ACCESS_RESTRICTED]: [1, LEVER_TYPE_NUMBER],
  [LEVER_SELFIE_DELETE_PROMPT_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SELFIE_VERIFICATION_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SELFIE_VERIFICATION_BIOMETRIC_CONSENT_SCREEN_VERSION]: [0, LEVER_TYPE_NUMBER],
  [LEVER_ID_VERIFICATION_VARIANT]: [0, LEVER_TYPE_NUMBER],
  [LEVER_SHIELD_FAB_GARBO_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SHIELD_FAB_REPORT_USER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SHOW_WEEKLY_SAVINGS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SPARKS_WEB]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SUB_DISCOUNT_PERSONALIZED_OFFER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SMS_AUTH_LOGIN_SUPPRESSED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_INSTAGRAM_IS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SPOTIFY_IS_ENABLED]: [true, LEVER_TYPE_BOOLEAN],
  [LEVER_SUPERLIKE_ATTACH_MESSAGE_RATE_LIMIT]: [0, LEVER_TYPE_NUMBER],
  [LEVER_SUPERLIKE_ATTACH_MESSAGE_TARGETING]: ['', LEVER_TYPE_STRING],
  [LEVER_SUPERLIKE_ATTACH_MESSAGE_TIME_LIMIT]: [0, LEVER_TYPE_NUMBER],
  [LEVER_SUPERLIKE_UPSELL_HIGH_RSRR_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_SUPERLIKE_UPSELL_HIGH_RSRR_RATE_LIMIT]: ['SESSION', LEVER_TYPE_STRING],
  [LEVER_FIRST_IMPRESSIONS_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_THEME_OVERRIDE]: ['sparks', LEVER_TYPE_STRING],
  [LEVER_TOP_NAV_V2_DYNAMIC_ORDER]: ['recs,explore,goldHome,matches,profile', LEVER_TYPE_STRING],
  [LEVER_WEB_DISCOUNT_BANNER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_WEB_DISCOUNT_LOGIN_BANNER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_WEB_DISCOUNT_PILL_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_WEB_PAYMENT_ENABLED]: [isEnabled('webPayment'), LEVER_TYPE_BOOLEAN],
  [LEVER_WEEKLY_GOLD_TO_PLATINUM_OFFER_ENABLED]: [false, LEVER_TYPE_BOOLEAN],
  [LEVER_MATCHMAKER_SUPERLIKES_OFFER]: [isEnabled('matchmakerSuperLikeOffer'), LEVER_TYPE_BOOLEAN],
} as const;

export type RegisteredLeverValues = typeof REGISTERED_LEVER_VALUES;
export type RegisteredLeverValuesEntry = RegisteredLeverValues[keyof RegisteredLeverValues];
export type RegisteredLeverKeys = keyof typeof REGISTERED_LEVER_VALUES;

/*
  Somewhat hacky - this ensures that values in REGISTERED_LEVER_VALUES are of the correct type
  without having to explicitly declare the type of the keys, allowing us to extract a union of keys
  later (see LeverKey in types.ts). This variable is never used and solely exists to raise a
  type error if any lever entry in the object does not comply.
*/
// eslint-disable-next-line @typescript-eslint/no-unused-vars
const REGISTERED_LEVER_VALUES_TYPE_GUARD: RegisteredLeverValuesEntry extends readonly [
  LeverConfigValue,
  LeverConfigType
]
  ? true
  : false = true;

/**
 * Any levers included here will have their value locked on first read. This locked value will be
 * returned for subsequent reads until they are unlocked due to the start of a new session.
 */
export const SESSION_LOCKED_LEVERS: Set<RegisteredLeverKeys> = new Set([]);
